import { useQuery } from "@tanstack/react-query";

import {
  buildRequestLogQueryPayload,
  type UseStructuredRequestsPaginatedOptions,
} from "./requests/useStructuredRequestsPaginated";

import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import { useRequestLogsWorkspaceId } from "@/components/RequestsAnalytics/hooks/useRequestLogsWorkspaceId";
import { useUserToken } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export enum AnalyticsChartIntervalValue {
  OneSecond = "1s",
  FifteenSeconds = "15s",
  OneMinute = "1m",
  FiveMinutes = "5m",
  FifteenMinutes = "15m",
  Hour = "1h",
  Day = "day",
  Week = "week",
  Month = "month",
}

export interface AnalyticsChartInterval {
  interval: AnalyticsChartIntervalValue;
  bucketSizeMs: number;
  bucketMinutes?: number;
}

export interface ToolLatencyRow {
  toolName: string;
  requests: number;
  avgLatencySeconds: number | null;
  minLatencySeconds: number | null;
  maxLatencySeconds: number | null;
}

export interface RequestAnalyticsResponse {
  chartInterval: AnalyticsChartInterval;
  averageLatency: number;
  totalCost: number;
  totalRequests: number;
  totalTokens: number;
  totalCachedTokens: number;
  totalThinkingTokens: number;
  cacheTokenRatio: number | null;
  stats: {
    date: string;
    dateLabel?: string;
    requests: number;
    tokens: number;
    inputTokens?: number;
    outputTokens?: number;
    cost: number;
    latency: number;
    cachedTokens?: number;
    thinkingTokens?: number;
    traceShare?: number;
    statusCounts?: Record<string, number>;
    outputShapeCounts?: {
      json: number;
      toolCall: number;
      plainText: number;
    };
    avgTurnCount?: number;
    avgToolCallCount?: number;
    toolCallCountP95?: number | null;
  }[];
  mostUsedModels: [string, number][];
  modelRequestsByDay: Record<string, [string, number][]>;
  mostUsedPromptTemplates: {
    promptId: string;
    promptName?: string;
    requests: number;
  }[];
  promptTemplateRequestsByDay: Record<string, [string, number][]>;
  providerRequestsByDay: Record<string, [string, number][]>;
  latency: Record<string, Record<string, number>>;
  latencyByModelByDay: Record<string, Record<string, [string, number][]>>;
  latencyByPromptTemplateByDay: Record<string, Record<string, [string, number][]>>;
  latencyByProviderByDay: Record<string, Record<string, [string, number][]>>;
  errorTypes: { errorType: string; requests: number }[];
  providerBreakdown: {
    provider: string;
    requests: number;
    cost: number;
    inputTokens: number;
    outputTokens: number;
  }[];
  promptBreakdown: {
    promptId: string;
    promptName: string;
    requests: number;
    cost: number;
    tokens: number;
  }[];
  tagsBreakdown: { tag: string; requests: number; cost: number }[];
  metadataKeysTop: { key: string; requests: number }[];
  metadataValueBreakdown: {
    key: string;
    value: string;
    label: string;
    requests: number;
    cost: number;
  }[];
  outputKeysTop: { key: string; requests: number }[];
  toolsLatency: ToolLatencyRow[];
  toolsUsageBars: { name: string; requests: number }[];
}

const DAY_BUCKET_SIZE_MS = 86_400_000;

const emptyAnalytics: RequestAnalyticsResponse = {
  chartInterval: {
    interval: AnalyticsChartIntervalValue.Day,
    bucketSizeMs: DAY_BUCKET_SIZE_MS,
  },
  averageLatency: 0,
  totalCost: 0,
  totalRequests: 0,
  totalTokens: 0,
  totalCachedTokens: 0,
  totalThinkingTokens: 0,
  cacheTokenRatio: null,
  stats: [],
  mostUsedModels: [],
  modelRequestsByDay: {},
  mostUsedPromptTemplates: [],
  promptTemplateRequestsByDay: {},
  providerRequestsByDay: {},
  latency: {
    average_latency: {},
    p50_latency: {},
    p90_latency: {},
    p95_latency: {},
  },
  latencyByModelByDay: {},
  latencyByPromptTemplateByDay: {},
  latencyByProviderByDay: {},
  errorTypes: [],
  providerBreakdown: [],
  promptBreakdown: [],
  tagsBreakdown: [],
  metadataKeysTop: [],
  metadataValueBreakdown: [],
  outputKeysTop: [],
  toolsLatency: [],
  toolsUsageBars: [],
};

export const useRequestAnalytics = (searchParams: UseStructuredRequestsPaginatedOptions) => {
  const workspaceId = useRequestLogsWorkspaceId();
  const userToken = useUserToken();

  const query = useQuery<RequestAnalyticsResponse>(
    [queryKeys.requests, "analytics", userToken, workspaceId, searchParams],
    () =>
      fetch(ENDPOINTS.post_requests_analytics(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(buildRequestLogQueryPayload(searchParams)),
      }).then(handleResponse),
    {
      enabled: !!userToken && !!workspaceId,
      placeholderData: emptyAnalytics,
      retry: 3,
    },
  );

  return {
    ...query,
    data: {
      ...emptyAnalytics,
      ...(query.data ?? {}),
      metadataValueBreakdown: query.data?.metadataValueBreakdown ?? [],
    },
  };
};

export interface MetadataCostBreakdownResponse {
  metadataValueBreakdown: RequestAnalyticsResponse["metadataValueBreakdown"];
}

export const useMetadataCostBreakdown = (
  searchParams: UseStructuredRequestsPaginatedOptions,
  metadataCostBreakdownKey: string | undefined,
  enabled: boolean,
) => {
  const workspaceId = useRequestLogsWorkspaceId();
  const userToken = useUserToken();

  return useQuery<RequestAnalyticsResponse["metadataValueBreakdown"]>(
    [
      queryKeys.requests,
      "metadata-cost-breakdown",
      userToken,
      workspaceId,
      searchParams,
      metadataCostBreakdownKey,
    ],
    async () => {
      if (!userToken || !workspaceId || !metadataCostBreakdownKey) {
        return [];
      }

      const response: MetadataCostBreakdownResponse = await fetch(
        ENDPOINTS.post_request_metadata_cost_breakdown(workspaceId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(
            buildRequestLogQueryPayload({
              ...searchParams,
              metadataCostBreakdownKey,
            }),
          ),
        },
      ).then(handleResponse);

      return response.metadataValueBreakdown ?? [];
    },
    {
      enabled: enabled && !!userToken && !!workspaceId && !!metadataCostBreakdownKey,
      keepPreviousData: true,
      retry: 3,
    },
  );
};
